import { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { CheckCircle, WifiOff, Maximize2, Minimize2 } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import useAdaptivePolling from "@/hooks/useAdaptivePolling";
import { enqueueVote, flushQueue, getQueueLength } from "@/utils/offlineVoteQueue";
import { toggleFullscreen, getFullscreenElement, onFullscreenChange } from "@/utils/fullscreen";
import { useAppTheme } from "@/context/AppThemeContext";
import { ThemeToggle } from "@/components/ThemeToggle";
import useTimerWS from "@/hooks/useTimerWS";
import TimerDisplay from "@/components/timer/TimerDisplay";
import { useEvent } from "@/components/EventLayout";
import useComms from "@/hooks/useComms";
import JudgePopup from "@/components/comms/JudgePopup";
import { BattleResults, CircleResults, allVotesRevealed } from "@/components/judge/ResultsScreen";
import { LifeBuoy } from "lucide-react";

import { BACKEND_URL } from '../utils/apiBase';
const API = `${BACKEND_URL}/api`;

const SLOT_COLORS = ['#335eea', '#d35680', '#2ecc71', '#f39c12'];
const BG_LIGHT = '#f7f7fa';
const BG_DARK = 'linear-gradient(160deg, #0a0a0f 0%, #12101f 50%, #0f0d1a 100%)';

/* Detect orientation */
const useOrientation = () => {
  const get = () => typeof window !== 'undefined' && window.innerWidth > window.innerHeight ? 'landscape' : 'portrait';
  const [o, setO] = useState(get);
  useEffect(() => {
    const h = () => setO(get());
    window.addEventListener('resize', h);
    window.addEventListener('orientationchange', h);
    return () => { window.removeEventListener('resize', h); window.removeEventListener('orientationchange', h); };
  }, []);
  return o;
};

/* Avatar with initial fallback */
const Avatar = ({ name, photoUrl, size, textColor = '#fff' }) => {
  const initial = (name || '?')[0].toUpperCase();
  const colors = ['#335eea', '#d35680', '#2ecc71', '#f39c12', '#8b5cf6', '#06b6d4'];
  const bg = colors[initial.charCodeAt(0) % colors.length];
  const s = { width: size, height: size, borderRadius: '50%', border: '2px solid rgba(128,128,128,0.2)', flexShrink: 0 };
  if (photoUrl) return <img src={photoUrl} alt={name} style={{ ...s, objectFit: 'cover' }} />;
  return <div style={{ ...s, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: textColor, fontSize: `calc(${size} * 0.45)`, fontFamily: 'Inter, system-ui, sans-serif' }}>{initial}</div>;
};

/* Stable shell wrapper at module scope. Declaring it inside the component would
 * create a new function reference on every render and force React to unmount /
 * remount the entire subtree (which was firing the JudgePopup notification
 * sound on every parent render). */
const CSS_RESET = `*,*::before,*::after{box-sizing:border-box}html,body{margin:0;padding:0;overflow:hidden!important;width:100vw;height:100dvh;height:100vh}@keyframes pulse{0%,100%{opacity:1}50%{opacity:.3}}`;
const Shell = ({ children, center, bg }) => (
  <div style={{ height: '100dvh', width: '100vw', overflow: 'hidden', background: bg, display: center ? 'flex' : 'block', alignItems: center ? 'center' : undefined, justifyContent: center ? 'center' : undefined, padding: center ? 16 : 0, position: 'relative' }}>
    <style>{CSS_RESET}</style>
    {children}
  </div>
);

const JudgePage = () => {
  const { judgeNumber } = useParams();
  const { t } = useI18n();
  const orientation = useOrientation();
  const { mode: appTheme } = useAppTheme();
  const isDark = appTheme === 'dark';
  const BG = isDark ? BG_DARK : BG_LIGHT;
  const TXT = isDark ? '#fff' : '#1a1a2e';
  const TXT_MUTED = isDark ? 'rgba(255,255,255,0.45)' : 'rgba(0,0,0,0.45)';
  const TXT_SECONDARY = isDark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.35)';
  const CARD_BG = isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)';
  const CARD_BORDER = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)';
  const INPUT_BG = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)';
  const INPUT_BORDER = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.12)';
  const BTN_BG = isDark ? 'linear-gradient(135deg, #335eea, #1e2bea)' : 'linear-gradient(135deg, #2563eb, #1d4ed8)';
  const ICON_MUTED = isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.35)';
  const BTN_DISABLED = isDark ? '#1e1e2e' : '#e5e7eb';
  const event = useEvent();
  const { battleTimers, circleTimers, settings: countdownSettings, refreshBattle, refreshCircle } = useTimerWS(event?.event_id);
  const countdownMode = countdownSettings?.countdown_mode === true;
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [judge, setJudge] = useState(null);
  const comms = useComms(
    isAuthenticated && event?.event_id ? event.event_id : null,
    "judge",
    judge?.judge_number || (judgeNumber ? parseInt(judgeNumber, 10) : null),
  );
  const [judgePreview, setJudgePreview] = useState(null);
  const [pinCode, setPinCode] = useState("");
  const [currentBattle, setCurrentBattle] = useState(null);
  const [currentCircle, setCurrentCircle] = useState(null);
  const [hasVoted, setHasVoted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [athlete1Score, setAthlete1Score] = useState(null);
  const [athlete2Score, setAthlete2Score] = useState(null);
  const [circleScores, setCircleScores] = useState({});
  const [tournaments, setTournaments] = useState([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingVotes, setPendingVotes] = useState(getQueueLength());
  const prevBattleRef = useRef(null);
  const prevCircleRef = useRef(null);
  const fpRef = useRef({ bId: null, bSt: null, bV: 0, cId: null, cSt: null, cV: 0 });
  // ------------------------------------------------------------------
  // Grace period against transient nulls.
  //
  // Problem: `GET /battle/current` (and /circle/current) briefly returns
  // null when the manager switches rounds, or on the first poll after a
  // page refresh / reconnect. If we IMMEDIATELY set the local state to
  // null the JudgePage renders the "waiting for reveal" fallback for one
  // poll cycle (800 ms – 3 s), then re-renders the active screen once
  // the next poll succeeds. That's the visible "blink" the manager and
  // judges complain about.
  //
  // Fix: keep the last-known good state and only clear it after
  // GRACE_MS of consecutive nulls. Anything else looks like a transient.
  // ------------------------------------------------------------------
  const GRACE_MS = 3500;
  const nullSinceRef = useRef({ battle: 0, circle: 0 });

  useEffect(() => { axios.get(`${API}/judges/${judgeNumber}/info`).then(r => setJudgePreview(r.data)).catch(() => {}); }, [judgeNumber]);
  useEffect(() => {
    const onOn = async () => { setIsOnline(true); const r = await flushQueue(); setPendingVotes(getQueueLength()); if (r.sent > 0) toast.success(`${r.sent} vote(s) envoyé(s)`); };
    const onOff = () => setIsOnline(false);
    window.addEventListener('online', onOn); window.addEventListener('offline', onOff);
    return () => { window.removeEventListener('online', onOn); window.removeEventListener('offline', onOff); };
  }, []);

  const toggleFs = () => { if (!document.fullscreenElement) { document.documentElement.requestFullscreen(); setIsFullscreen(true); } else { document.exitFullscreen(); setIsFullscreen(false); } };
  const loadTournaments = useCallback(async () => { try { setTournaments((await axios.get(`${API}/tournaments`)).data); } catch (_e) { /* keep last */ } }, []);
  const loadCurrentBattle = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/battle/current`);
      const fp = fpRef.current;
      const now = Date.now();
      if (res.data) {
        nullSinceRef.current.battle = 0;
        const { id: nI, status: nS } = res.data.battle || {}; const nV = res.data.votes?.length || 0;
        if (nI !== fp.bId || nS !== fp.bSt || nV !== fp.bV) { fp.bId = nI; fp.bSt = nS; fp.bV = nV; setCurrentBattle(res.data); setHasVoted(res.data.votes?.some(v => v.judge_id === judge?.id)); }
        // A battle is now current → clear any lingering circle state.
        if (fp.cId) { fp.cId = null; fp.cSt = null; fp.cV = 0; setCurrentCircle(null); nullSinceRef.current.circle = 0; }
      } else {
        // No current battle from the server: grace-period before clearing local state.
        if (fp.bId) {
          if (!nullSinceRef.current.battle) nullSinceRef.current.battle = now;
          if (now - nullSinceRef.current.battle >= GRACE_MS) {
            fp.bId = null; fp.bSt = null; fp.bV = 0; setCurrentBattle(null); nullSinceRef.current.battle = 0;
          }
        }
        try {
          const cr = await axios.get(`${API}/circle/current`);
          if (cr.data) {
            nullSinceRef.current.circle = 0;
            const { id: cI, status: cS } = cr.data.circle || {}; const cV = cr.data.votes?.length || 0;
            if (cI !== fp.cId || cS !== fp.cSt || cV !== fp.cV) { fp.cId = cI; fp.cSt = cS; fp.cV = cV; setCurrentCircle(cr.data); setHasVoted(cr.data.votes?.some(v => v.judge_id === judge?.id)); }
          } else if (fp.cId) {
            if (!nullSinceRef.current.circle) nullSinceRef.current.circle = now;
            if (now - nullSinceRef.current.circle >= GRACE_MS) {
              fp.cId = null; fp.cSt = null; fp.cV = 0; setCurrentCircle(null); setHasVoted(false); setAthlete1Score(null); setAthlete2Score(null); setCircleScores({}); nullSinceRef.current.circle = 0;
            }
          }
        } catch (_e) {
          // Circle endpoint transient error: keep the last known state, do NOT clear.
        }
      }
    } catch (_e) {
      // Battle endpoint transient error: keep the last known state, do NOT clear.
    }
  }, [judge?.id]);

  const loadData = useCallback(async () => { await loadCurrentBattle(); await loadTournaments(); }, [loadCurrentBattle, loadTournaments]);

  // FAST polling triggers during ALL live timer states (pre_start, pre_countdown,
  // running, paused) plus voting/revealing — same rule as the Big Screen —
  // so a lost WebSocket frame is caught within 800 ms by the next poll.
  const _battleTimerForPolling = currentBattle?.battle?.id ? battleTimers[currentBattle.battle.id] : null;
  const _circleTimerForPolling = currentCircle?.circle?.id ? circleTimers[currentCircle.circle.id] : null;
  const _liveTimerStatuses = ['pre_start', 'pre_countdown', 'running', 'paused'];
  const isActive = (
    currentBattle?.battle?.status === 'voting' ||
    currentBattle?.battle?.status === 'revealing' ||
    currentCircle?.circle?.status === 'voting' ||
    currentCircle?.circle?.status === 'revealing' ||
    _liveTimerStatuses.includes(_battleTimerForPolling?.status) ||
    _liveTimerStatuses.includes(_circleTimerForPolling?.status)
  );
  const pollingDataFn = useCallback(async () => {
    if (typeof navigator !== 'undefined' && !navigator.onLine) return;
    await loadData();
    // Belt-and-suspenders: force-refresh timer state via REST at every poll
    // cycle so the JudgePage always converges to the server state even if
    // some WebSocket frames were dropped (mobile browser throttling, wifi
    // blip, backgrounded tab, ...).
    const bId = currentBattle?.battle?.id;
    const cId = currentCircle?.circle?.id;
    if (bId) refreshBattle(bId);
    if (cId) refreshCircle(cId);
  }, [loadData, currentBattle?.battle?.id, currentCircle?.circle?.id, refreshBattle, refreshCircle]);
  useEffect(() => { if (isAuthenticated) loadData(); }, [isAuthenticated]);
  useAdaptivePolling(isAuthenticated ? pollingDataFn : () => Promise.resolve(), isAuthenticated && isActive);
  useEffect(() => onFullscreenChange(() => setIsFullscreen(!!getFullscreenElement())), []);
  useEffect(() => { const id = currentBattle?.battle?.id; if (id && prevBattleRef.current && id !== prevBattleRef.current) { setAthlete1Score(null); setAthlete2Score(null); setHasVoted(false); } prevBattleRef.current = id; }, [currentBattle?.battle?.id]);
  useEffect(() => { const id = currentCircle?.circle?.id; if (id && prevCircleRef.current && id !== prevCircleRef.current) { setCircleScores({}); setHasVoted(false); } prevCircleRef.current = id; }, [currentCircle?.circle?.id]);
  // Fetch initial timer state when current battle/circle becomes known (handles late connections)
  useEffect(() => { if (currentBattle?.battle?.id) refreshBattle(currentBattle.battle.id); }, [currentBattle?.battle?.id, refreshBattle]);
  useEffect(() => { if (currentCircle?.circle?.id) refreshCircle(currentCircle.circle.id); }, [currentCircle?.circle?.id, refreshCircle]);

  // ------------------------------------------------------------------
  // Instant state sync via the timer WebSocket.
  //
  // The `useTimerWS` hook receives push messages on every timer event
  // (start/pause/resume/finish/reveal/…). Whenever the timer STATUS
  // for the currently-tracked battle/circle changes, force an
  // immediate refetch of the current state — no need to wait for the
  // 800 ms poll cycle. This is what closes the last ~1 s of desync
  // observed by judges during round transitions.
  // ------------------------------------------------------------------
  const lastBattleTimerStatusRef = useRef(null);
  const lastCircleTimerStatusRef = useRef(null);
  useEffect(() => {
    if (!isAuthenticated) return;
    const bid = currentBattle?.battle?.id;
    const t = bid ? battleTimers[bid] : null;
    if (t && t.status !== lastBattleTimerStatusRef.current) {
      lastBattleTimerStatusRef.current = t.status;
      loadData();
    }
  }, [battleTimers, currentBattle?.battle?.id, isAuthenticated, loadData]);
  useEffect(() => {
    if (!isAuthenticated) return;
    const cid = currentCircle?.circle?.id;
    const t = cid ? circleTimers[cid] : null;
    if (t && t.status !== lastCircleTimerStatusRef.current) {
      lastCircleTimerStatusRef.current = t.status;
      loadData();
    }
  }, [circleTimers, currentCircle?.circle?.id, isAuthenticated, loadData]);

  // Refresh authoritative state on tab-visibility regain (Samsung / iPhone
  // background/foreground) and on network reconnect. The last-known state
  // stays visible during the request; the grace period above already
  // prevents any blink between the request start and its resolution.
  useEffect(() => {
    if (!isAuthenticated) return;
    const onWake = () => { if (document.visibilityState === 'visible') loadData(); };
    const onOnline2 = () => { loadData(); };
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('focus', onWake);
    window.addEventListener('online', onOnline2);
    return () => {
      document.removeEventListener('visibilitychange', onWake);
      window.removeEventListener('focus', onWake);
      window.removeEventListener('online', onOnline2);
    };
  }, [isAuthenticated, loadData]);

  // Heartbeat: send every 10s when authenticated to track judge connectivity
  useEffect(() => {
    if (!isAuthenticated) return;
    const sendHeartbeat = () => { axios.post(`${API}/judges/${judgeNumber}/heartbeat`).catch(() => {}); };
    sendHeartbeat();
    const iv = setInterval(sendHeartbeat, 10000);
    return () => clearInterval(iv);
  }, [isAuthenticated, judgeNumber]);

  const handleLogin = useCallback(async () => {
    try {
      const r = await axios.post(`${API}/judges/login`, { judge_number: parseInt(judgeNumber), pin_code: "" });
      setJudge(r.data);
      setIsAuthenticated(true);
      toast.success(t('judge.welcome', { name: r.data.first_name }));
    } catch (_e) {
      toast.error(t('judge.notFound') || 'Judge not found');
    }
  }, [judgeNumber, t]);

  // Auto-login: the URL carries the judge number, no PIN required.
  useEffect(() => {
    if (isAuthenticated || !judgeNumber) return;
    handleLogin();
  }, [judgeNumber, isAuthenticated, handleLogin]);

  // Determine if the timer is currently locking the vote
  const battleTimer = currentBattle?.battle?.id ? battleTimers[currentBattle.battle.id] : null;
  const circleTimer = currentCircle?.circle?.id ? circleTimers[currentCircle.circle.id] : null;
  const activeTimer = battleTimer || circleTimer;
  const timerLocksVote = countdownMode && activeTimer && activeTimer.status !== "done";

  const submitVote = async () => {
    // Idempotency guard: if a submission is already in flight, ignore.
    // Combined with the backend unique index this makes double-clicks safe.
    if (isSubmitting) return;
    if (timerLocksVote) { toast.error(t('timer.errorWaitVote')); return; }
    if (!athlete1Score || !athlete2Score) { toast.error(t('judge.selectScores')); return; }
    if (athlete1Score === athlete2Score) { toast.error(t('judge.uniqueScores')); return; }
    const url = `${API}/battle/${currentBattle.battle.id}/vote`;
    setIsSubmitting(true);
    try { await axios.post(url, { judge_id: judge.id, athlete1_score: athlete1Score, athlete2_score: athlete2Score }); toast.success(t('judge.voteSubmitted')); setHasVoted(true); loadCurrentBattle();
    } catch (e) { if (!navigator.onLine || e.message === 'Network Error') { enqueueVote({ type: 'battle', url, judge_id: judge.id, athlete1_score: athlete1Score, athlete2_score: athlete2Score }); setPendingVotes(getQueueLength()); toast.success(t('judge.voteQueued')); setHasVoted(true); } else { toast.error(e.response?.data?.detail || t('judge.errorSubmitting')); } }
    finally { setIsSubmitting(false); }
  };

  const submitCircleVote = async () => {
    if (isSubmitting) return;
    if (timerLocksVote) { toast.error(t('timer.errorWaitVote')); return; }
    const forfeited = currentCircle.circle.forfeited_ids || [];
    const active = currentCircle.athletes.filter(a => a && !forfeited.includes(a.id));
    if (!active.every(a => circleScores[a.id])) { toast.error(t('judge.scoreAllAthletes')); return; }
    const vals = Object.values(circleScores).filter(v => v !== undefined);
    if (new Set(vals).size !== vals.length) { toast.error(t('judge.uniqueScores')); return; }
    const url = `${API}/circle/${currentCircle.circle.id}/vote`;
    setIsSubmitting(true);
    try { await axios.post(url, { judge_id: judge.id, scores: circleScores }); toast.success(t('judge.voteSubmitted')); setHasVoted(true); loadCurrentBattle();
    } catch (e) { if (!navigator.onLine || e.message === 'Network Error') { enqueueVote({ type: 'circle', url, judge_id: judge.id, scores: circleScores }); setPendingVotes(getQueueLength()); toast.success(t('judge.voteQueued')); setHasVoted(true); } else { toast.error(e.response?.data?.detail || t('judge.errorSubmitting')); } }
    finally { setIsSubmitting(false); }
  };

  const isJudgeAssigned = () => {
    if (!judge) return true;
    if (currentBattle) { const bt = tournaments.find(t => t.id === currentBattle.battle?.tournament_id); if (!bt || !bt.assigned_judge_numbers?.length) return true; return bt.assigned_judge_numbers.includes(judge.judge_number); }
    if (currentCircle) { const nums = currentCircle.tournament?.assigned_judge_numbers || []; return nums.length === 0 || nums.includes(judge.judge_number); }
    return true;
  };

  const isLandscape = orientation === 'landscape';

  /* ========== Overlay: judge popup + persistent HELP / Theme / Fullscreen stack
   * (always on top, even over the timer-lock overlay z=100) ========== */
  const CommsOverlay = isAuthenticated ? (
    <>
      <JudgePopup
        message={comms.latestMessage}
        onDismiss={comms.dismissMessage}
        onReact={(type, mid) => comms.sendReaction(type, mid)}
        onHelp={() => comms.triggerHelp()}
      />
      <div
        style={{
          position: 'fixed', top: 12, left: 12,
          zIndex: 2147483000,
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
        }}
        data-testid="judge-quick-actions"
      >
        <button
          onClick={() => comms.triggerHelp().then(() => toast.success(t('comms.helpSent'))).catch(() => toast.error(t('common.error')))}
          data-testid="judge-help-button"
          style={{
            width: 56, height: 56, borderRadius: '50%',
            background: 'linear-gradient(135deg,#ef4444,#b91c1c)', color: 'white',
            border: 'none', cursor: 'pointer',
            boxShadow: '0 6px 24px rgba(239,68,68,.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          title={t('comms.helpButtonTitle')}
          aria-label="HELP"
        >
          <LifeBuoy size={26} />
        </button>
        <div
          style={{
            background: 'rgba(0,0,0,0.55)',
            backdropFilter: 'blur(8px)',
            borderRadius: 12,
            padding: '6px 4px',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
            border: '1px solid rgba(255,255,255,0.12)',
            boxShadow: '0 4px 16px rgba(0,0,0,.35)',
          }}
        >
          <ThemeToggle className="p-1" data-testid="judge-theme-toggle" />
          <button
            onClick={toggleFs}
            data-testid="judge-fullscreen-btn"
            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            aria-label="Fullscreen toggle"
            style={{
              width: 32, height: 32, borderRadius: 8,
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: 'rgba(255,255,255,0.85)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            {isFullscreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
          </button>
        </div>
      </div>
    </>
  ) : null;

  /* ========== Timer-lock overlay (rendered before CommsOverlay so popup/HELP stay on top) ========== */
  const TimerLockOverlay = timerLocksVote ? (() => {
    let activeName = null;
    if (activeTimer?.kind === 'battle' && currentBattle) {
      if (activeTimer.current_athlete === 'a1') activeName = currentBattle.athlete1?.name;
      else if (activeTimer.current_athlete === 'a2') activeName = currentBattle.athlete2?.name;
    } else if (activeTimer?.kind === 'circle' && currentCircle) {
      const seg = activeTimer.sequence?.[activeTimer.segment_index];
      if (seg) {
        const aid = currentCircle.circle?.athlete_ids?.[seg.position - 1];
        const a = currentCircle.athletes?.find(x => x?.id === aid);
        activeName = a?.name;
      }
    }
    const roundNum = activeTimer?.kind === 'circle'
      ? (activeTimer?.sequence?.[activeTimer?.segment_index]?.round || '—')
      : (activeTimer?.round || 1);
    return (
      <div style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(0,0,0,0.92)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 16 }} data-testid="judge-timer-lock">
        <div style={{ color: '#fbbf24', fontWeight: 700, fontSize: '1.4rem', marginBottom: 8, textAlign: 'center' }} data-testid="judge-timer-round">
          {t('timer.round')} {roundNum}
        </div>
        {activeName && (
          <div style={{ color: '#ffffff', fontWeight: 600, fontSize: '1.1rem', marginBottom: 16, textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.05em' }} data-testid="judge-timer-active-name">
            {activeName}
          </div>
        )}
        <TimerDisplay timer={activeTimer} variant="huge" playSounds={false} showProgress={false} />
        <div style={{ color: 'rgba(255,255,255,0.7)', marginTop: 24, fontSize: '0.9rem', textAlign: 'center' }}>
          {t('timer.voteAvailableAfterRounds')}
        </div>
      </div>
    );
  })() : null;

  /* ========== Status icons (wifi/queue only — theme & fullscreen are in the
   * always-visible top-left CommsOverlay stack) ========== */
  const TopRight = () => (
    <div style={{ position: 'absolute', top: 4, right: 8, display: 'flex', alignItems: 'center', gap: 6, zIndex: 10 }}>
      {!isOnline && <WifiOff size={13} color="#f87171" />}
      {pendingVotes > 0 && <span style={{ fontSize: 10, color: '#fbbf24' }}>{pendingVotes}</span>}
    </div>
  );

  /* ========== LOGIN ========== */
  if (!isAuthenticated) {
    return (
      <Shell center bg={BG}>
        <div style={{ position: 'absolute', top: 8, right: 12, zIndex: 10 }}><ThemeToggle className="p-1" /></div>
        <Card className="p-6 sm:p-8 glass" style={{ maxWidth: 360, width: '100%', borderRadius: 20, border: `1px solid ${CARD_BORDER}` }} data-testid="judge-login-card">
          <div style={{ textAlign: 'center', marginBottom: 16 }}>
            <div style={{ margin: '0 auto', width: 'fit-content' }}>
              <Avatar name={judgePreview?.first_name || judgeNumber} photoUrl={judgePreview?.photo_url || ''} size="64px" />
            </div>
          </div>
          <h1 style={{ textAlign: 'center', fontSize: '1.15rem', fontWeight: 700, color: TXT, margin: '0 0 2px', fontFamily: 'Inter, system-ui, sans-serif' }}>{t('judge.loginTitle', { num: judgeNumber })}</h1>
          {judgePreview?.first_name && <p style={{ textAlign: 'center', fontSize: '0.85rem', color: TXT_MUTED, margin: '0 0 16px' }} data-testid="judge-preview-name">{judgePreview.first_name}</p>}
          {!judgePreview?.first_name && <div style={{ height: 16 }} />}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p style={{ textAlign: 'center', fontSize: '0.85rem', color: ICON_MUTED, margin: '0 0 4px' }}>
              {t('judge.autoConnecting') || 'Connecting…'}
            </p>
            <Button onClick={handleLogin} className="w-full h-12 text-sm font-semibold rounded-xl" style={{ background: BTN_BG }} data-testid="login-btn">{t('judge.login')}</Button>
          </div>
        </Card>
      </Shell>
    );
  }

  /* ========== NOT ASSIGNED ========== */
  if ((currentBattle || currentCircle) && !isJudgeAssigned()) {
    return (<><Shell center bg={BG}><Card className="p-6 glass text-center" style={{ borderRadius: 20 }} data-testid="not-assigned-card"><h2 className="text-lg font-bold mb-2">{t('judge.welcome', { name: judge.first_name })}</h2><p className="text-sm" style={{ color: ICON_MUTED }}>Not assigned</p></Card></Shell>{TimerLockOverlay}{CommsOverlay}</>);
  }

  /* ========== RESULTS SCREEN
   *
   * Shown to ALL judges (whether assigned or not — 5a) as soon as every
   * vote has been revealed. Stays visible until the next battle/circle
   * kicks in — the id-change effects above already clear `hasVoted`
   * and vote state on transition, so this simply falls back to the
   * usual screens when a new id arrives.
   * ================================================================ */
  const battleRevealed = currentBattle && allVotesRevealed(currentBattle.votes);
  const circleRevealed = currentCircle && allVotesRevealed(currentCircle.votes);
  if (battleRevealed) {
    return (
      <>
        <Shell center bg={BG}>
          <BattleResults
            athlete1={currentBattle.athlete1}
            athlete2={currentBattle.athlete2}
            votes={currentBattle.votes}
            isDark={isDark}
          />
        </Shell>
        {TimerLockOverlay}
        {CommsOverlay}
      </>
    );
  }
  if (circleRevealed) {
    return (
      <>
        <Shell center bg={BG}>
          <CircleResults
            athletes={currentCircle.athletes}
            votes={currentCircle.votes}
            forfeitedIds={currentCircle.circle?.forfeited_ids || []}
            isDark={isDark}
          />
        </Shell>
        {TimerLockOverlay}
        {CommsOverlay}
      </>
    );
  }

  /* ==================================================================
   *  FALLBACK / WAITING SCREEN
   *
   *  Only render the "Bienvenue … en attente" fallback when there is
   *  truly nothing to display:
   *    - no battle AND no circle at all (idle), OR
   *    - the current entity is finished/completed.
   *
   *  Previously this fallback also triggered during `running`,
   *  `pre_countdown` and `revealing` states, which caused the visible
   *  "blink to welcome screen" every time the manager transitioned a
   *  round — the exact bug reported by judges in a live event.
   * ================================================================ */
  const battleActive = currentBattle && !['completed', 'finished'].includes(currentBattle.battle?.status);
  const circleActive = currentCircle && !['completed', 'finished'].includes(currentCircle.circle?.status);
  if (!battleActive && !circleActive) {
    return (
      <>
        <Shell center bg={BG}>
          <div style={{ position: 'relative' }}><TopRight /></div>
          <Card className="p-6 glass text-center" style={{ borderRadius: 20 }} data-testid="no-battle-card">
            <div style={{ width: 40, height: 40, borderRadius: '50%', background: CARD_BG, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px' }}><div style={{ width: 8, height: 8, borderRadius: '50%', background: '#4ade80', animation: 'pulse 2s infinite' }} /></div>
            <h2 style={{ fontSize: '1rem', fontWeight: 600, color: TXT, margin: '0 0 4px' }}>{t('judge.welcome', { name: judge.first_name })}</h2>
            <p style={{ fontSize: '0.75rem', color: TXT_SECONDARY, margin: 0 }}>{t('judge.waitingReveal')}</p>
          </Card>
        </Shell>
        {TimerLockOverlay}
        {CommsOverlay}
      </>
    );
  }

  /* ========== VOTED ========== */
  if (hasVoted) {
    return (<><Shell center bg={BG}><Card className="p-6 glass text-center" style={{ borderRadius: 20 }} data-testid="voted-card"><CheckCircle style={{ width: 40, height: 40, color: '#10b981', margin: '0 auto 12px' }} /><h2 style={{ fontSize: '1rem', fontWeight: 600, margin: '0 0 4px' }}>{t('judge.alreadyVoted')}</h2><p style={{ fontSize: '0.75rem', color: TXT_SECONDARY, margin: 0 }}>{t('judge.welcome', { name: judge.first_name })}</p></Card></Shell>{TimerLockOverlay}{CommsOverlay}</>);
  }

  /* ========== SCORE GRID (5 cols x 2 rows, fills container) ========== */
  const ScoreGrid = ({ athleteId, idx, selectedScore, onSelect, blockedScores }) => {
    const color = SLOT_COLORS[idx] || SLOT_COLORS[0];
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gridTemplateRows: '1fr 1fr', gap: 'clamp(3px, 0.8vh, 8px)', width: '100%', height: '100%', padding: 'clamp(2px, 0.4vh, 6px) clamp(4px, 0.5vw, 10px)', alignContent: 'center' }} data-testid={`score-grid-${athleteId}`}>
        {[1,2,3,4,5,6,7,8,9,10].map(n => {
          const isSel = selectedScore === n;
          const isBlocked = !isSel && blockedScores.includes(n);
          const isLocked = timerLocksVote;
          return (
            <button key={n} onClick={() => !isBlocked && !isLocked && onSelect(n === selectedScore ? null : n)} disabled={isBlocked || isLocked}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                borderRadius: 10,
                border: isSel ? `2px solid ${color}` : `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.1)'}`,
                background: isSel ? color : isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                color: isSel ? '#fff' : (isBlocked || isLocked) ? (isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.15)') : (isDark ? 'rgba(255,255,255,0.75)' : 'rgba(0,0,0,0.75)'),
                fontWeight: 700, fontSize: 'clamp(1rem, 3.5vh, 2.2rem)', fontFamily: 'Inter, system-ui, sans-serif',
                cursor: (isBlocked || isLocked) ? 'not-allowed' : 'pointer', opacity: isLocked ? 0.25 : isBlocked ? 0.2 : 1,
                transition: 'background 0.15s, transform 0.1s, box-shadow 0.15s',
                transform: isSel ? 'scale(1.04)' : 'scale(1)',
                boxShadow: isSel ? `0 0 20px ${color}40` : 'none',
                maxHeight: 120,
              }}
              data-testid={`athlete${idx}-score-${n}`}
            >{n}</button>
          );
        })}
      </div>
    );
  };

  /* ========== ATHLETE CARD (flex column: info 3fr + scores 4fr) ========== */
  const AthleteCard = ({ athlete, idx, selectedScore, onSelect, blockedScores, forfeited }) => (
    <div style={{ display: 'flex', flexDirection: 'column', borderRadius: 14, background: 'rgba(255,255,255,0.02)', border: `1px solid ${CARD_BORDER}`, overflow: 'hidden', minHeight: 0, flex: '1 1 0', position: 'relative' }} data-testid={forfeited ? `circle-score-athlete-${athlete.id}` : `score-athlete-${athlete.id}`}>
      {/* Position badge */}
      <div style={{ position: 'absolute', top: 4, left: 6, background: isDark ? 'rgba(124,58,237,0.5)' : 'rgba(37,99,235,0.15)', color: isDark ? '#e0d4ff' : '#2563eb', fontSize: 'clamp(0.5rem, 1.2vh, 0.7rem)', fontWeight: 700, borderRadius: 6, padding: '1px 6px', lineHeight: 1.5, zIndex: 2, letterSpacing: '0.5px' }} data-testid={`position-badge-${idx+1}`}>
        P{idx + 1}
      </div>
      {/* INFO: 3/7 of card height */}
      <div style={{ flex: '3 1 0', minHeight: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '0 4px', gap: 'clamp(4px, 0.8vh, 10px)', opacity: forfeited ? 0.35 : 1, filter: forfeited ? 'grayscale(1)' : 'none' }}>
        <Avatar name={athlete.name} photoUrl={athlete.photo_url} size={`clamp(36px, 10vh, 90px)`} />
        <div style={{ textAlign: 'center', lineHeight: 1.25 }}>
          <p style={{ margin: 0, fontSize: 'clamp(0.7rem, 2.2vh, 1.2rem)', fontWeight: 700, color: TXT, fontFamily: 'Inter, system-ui, sans-serif', textDecoration: forfeited ? 'line-through' : 'none', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 'min(30vw, 250px)' }}>
            {athlete.name}{athlete.world_ranking ? ` #${athlete.world_ranking}` : ''}
          </p>
          <p style={{ margin: '2px 0 0', fontSize: 'clamp(0.5rem, 1.3vh, 0.7rem)', color: ICON_MUTED, fontWeight: 500 }}>
            {forfeited ? t('display.forfait') : athlete.country}
          </p>
        </div>
      </div>
      {/* SCORES: 4/7 of card height */}
      {forfeited ? (
        <div style={{ flex: '4 1 0', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ef4444', fontWeight: 700, fontSize: 'clamp(0.7rem, 1.8vh, 1rem)', opacity: 0.5, minHeight: 0 }}>{t('display.forfait')}</div>
      ) : (
        <div style={{ flex: '4 1 0', minHeight: 0, padding: '0 0 clamp(2px, 0.3vh, 6px)' }}>
          <ScoreGrid athleteId={athlete.id} idx={idx} selectedScore={selectedScore} onSelect={onSelect} blockedScores={blockedScores} />
        </div>
      )}
    </div>
  );

  /* ========== CIRCLE INTERFACE (voting / running / revealing / pre_countdown) ========== */
  // Render the circle vote UI whenever a circle is active. Buttons are
  // locked when the judge already voted (`hasVoted`) or when the timer
  // hasn't reached "done" yet (`timerLocksVote`). This is what keeps the
  // judge screen stable across round transitions — no more blink to the
  // welcome screen.
  if (circleActive && currentCircle?.athletes?.length) {
    const forfeited = currentCircle.circle.forfeited_ids || [];
    const allAthletes = currentCircle.athletes.filter(a => a);
    const activeAthletes = allAthletes.filter(a => !forfeited.includes(a.id));
    const usedScores = [];
    Object.entries(circleScores).forEach(([, v]) => { if (v !== undefined) usedScores.push(v); });
    const allScored = activeAthletes.every(a => circleScores[a.id]);
    const noDupes = new Set(usedScores).size === usedScores.length;
    const canSubmit = allScored && noDupes && currentCircle.circle.status === 'voting' && !timerLocksVote;
    const n = allAthletes.length;
    const use2x2 = n === 4;

    return (
      <>
        <Shell bg={BG}>
          <div style={{ display: 'flex', flexDirection: 'column', height: '100dvh', maxWidth: isLandscape ? 1100 : 600, margin: '0 auto', padding: '0 clamp(4px, 0.6vw, 12px)' }} data-testid="circle-voting-interface">
            {/* HEADER: 10vh */}
            <div style={{ height: '10vh', minHeight: 40, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', position: 'relative', flexShrink: 0 }}>
              <TopRight />
              <p style={{ margin: 0, fontSize: 'clamp(0.75rem, 2vh, 1.1rem)', fontWeight: 700, color: TXT, fontFamily: 'Inter, system-ui, sans-serif' }} data-testid="judge-header-name">{judge.first_name} — {t('judge.loginTitle', { num: judge.judge_number })}</p>
              <p style={{ margin: '1px 0 0', fontSize: 'clamp(0.5rem, 1.2vh, 0.75rem)', fontWeight: 500, color: TXT_SECONDARY }}>{t('circle.title')} {currentCircle.circle.circle_number} · {t('judge.scoreAllAthletes')} (1-10)</p>
            </div>

            {/* ATHLETES: flex-1 (takes 80vh) */}
            <div style={{
              flex: '1 1 0', minHeight: 0, overflow: 'hidden',
              display: 'grid',
              gridTemplateColumns: use2x2 ? 'repeat(2, 1fr)' : `repeat(${isLandscape ? n : Math.min(n, 2)}, 1fr)`,
              gridTemplateRows: use2x2 ? '1fr 1fr' : undefined,
              gap: 'clamp(3px, 0.4vw, 8px)',
            }}>
              {allAthletes.map((athlete, idx) => {
                const isForf = forfeited.includes(athlete.id);
                const sel = circleScores[athlete.id];
                const blocked = usedScores.filter(v => v !== sel);
                return <AthleteCard key={athlete.id} athlete={athlete} idx={idx} selectedScore={sel} onSelect={v => setCircleScores(prev => ({ ...prev, [athlete.id]: v }))} blockedScores={blocked} forfeited={isForf} />;
              })}
            </div>

            {/* VALIDATE: 10vh */}
            <div style={{ height: '10vh', minHeight: 40, display: 'flex', alignItems: 'center', padding: '0 clamp(4px, 1vw, 16px)', flexShrink: 0 }}>
              <button onClick={submitCircleVote} disabled={!canSubmit}
                style={{ width: '100%', height: 'clamp(34px, 6vh, 50px)', border: 'none', borderRadius: 14, fontWeight: 700, fontSize: 'clamp(0.75rem, 2vh, 1.05rem)', fontFamily: 'Inter, system-ui, sans-serif', color: TXT, cursor: canSubmit ? 'pointer' : 'not-allowed', background: canSubmit ? 'linear-gradient(135deg, #10b981, #059669)' : BTN_DISABLED, opacity: canSubmit ? 1 : 0.4, transition: 'opacity 0.2s' }}
                data-testid="submit-vote-btn"
              >{t('judge.submitVote')}</button>
            </div>
          </div>
        </Shell>
        {TimerLockOverlay}
        {CommsOverlay}
      </>
    );
  }

  /* ========== BATTLE INTERFACE (voting / running / revealing / pre_countdown) ========== */
  // Same principle as circle: keep the vote UI mounted for every active
  // battle status so judges never lose their screen during transitions.
  if (!battleActive || !currentBattle?.athlete1 || !currentBattle?.athlete2) {
    // No active battle to render → safety fallback (should be rare thanks
    // to the earlier grace-period + fallback screen above).
    return (
      <>
        <Shell center bg={BG}>
          <Card className="p-6 glass text-center" style={{ borderRadius: 20 }} data-testid="no-battle-card">
            <h2 style={{ fontSize: '1rem', fontWeight: 600, color: TXT, margin: '0 0 4px' }}>{t('judge.welcome', { name: judge.first_name })}</h2>
            <p style={{ fontSize: '0.75rem', color: TXT_SECONDARY, margin: 0 }}>{t('judge.waitingReveal')}</p>
          </Card>
        </Shell>
        {TimerLockOverlay}
        {CommsOverlay}
      </>
    );
  }
  const athletes = [currentBattle.athlete1, currentBattle.athlete2];
  const scores = [athlete1Score, athlete2Score];
  const setters = [setAthlete1Score, setAthlete2Score];
  const canSubmitBattle = athlete1Score && athlete2Score && athlete1Score !== athlete2Score && currentBattle.battle.status === 'voting' && !timerLocksVote;

  return (
    <>
      <Shell bg={BG}>
        <div style={{ display: 'flex', flexDirection: 'column', height: '100dvh', maxWidth: isLandscape ? 900 : 500, margin: '0 auto', padding: '0 clamp(4px, 0.6vw, 12px)' }} data-testid="voting-interface">
          {/* HEADER 10vh */}
          <div style={{ height: '10vh', minHeight: 40, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', position: 'relative', flexShrink: 0 }}>
            <TopRight />
            <p style={{ margin: 0, fontSize: 'clamp(0.75rem, 2vh, 1.1rem)', fontWeight: 700, color: TXT, fontFamily: 'Inter, system-ui, sans-serif' }} data-testid="judge-header-name">{judge.first_name} — {t('judge.loginTitle', { num: judge.judge_number })}</p>
            <p style={{ margin: '1px 0 0', fontSize: 'clamp(0.5rem, 1.2vh, 0.75rem)', fontWeight: 500, color: TXT_SECONDARY }}>{t('judge.scoreAllAthletes')} (1-10)</p>
          </div>

          {/* ATHLETES: flex-1, side-by-side in landscape, stacked in portrait */}
          <div style={{
            flex: '1 1 0', minHeight: 0, overflow: 'hidden',
            display: 'flex',
            flexDirection: isLandscape ? 'row' : 'column',
            gap: 'clamp(3px, 0.5vw, 10px)',
          }}>
            {athletes.map((athlete, idx) => {
              const sel = scores[idx];
              const otherScore = scores[1 - idx];
              const blocked = otherScore ? [otherScore] : [];
              return <AthleteCard key={athlete.id} athlete={athlete} idx={idx} selectedScore={sel} onSelect={v => setters[idx](v)} blockedScores={blocked} forfeited={false} />;
            })}
          </div>

          {/* VALIDATE 10vh */}
          <div style={{ height: '10vh', minHeight: 40, display: 'flex', alignItems: 'center', padding: '0 clamp(4px, 1vw, 16px)', flexShrink: 0 }}>
            <button onClick={submitVote} disabled={!canSubmitBattle}
              style={{ width: '100%', height: 'clamp(34px, 6vh, 50px)', border: 'none', borderRadius: 14, fontWeight: 700, fontSize: 'clamp(0.75rem, 2vh, 1.05rem)', fontFamily: 'Inter, system-ui, sans-serif', color: TXT, cursor: canSubmitBattle ? 'pointer' : 'not-allowed', background: canSubmitBattle ? 'linear-gradient(135deg, #10b981, #059669)' : BTN_DISABLED, opacity: canSubmitBattle ? 1 : 0.4, transition: 'opacity 0.2s' }}
              data-testid="submit-vote-btn"
            >{t('judge.submitVote')}</button>
          </div>
        </div>
      </Shell>
      {TimerLockOverlay}
      {CommsOverlay}
    </>
  );
};

export default JudgePage;
